import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Star, TrendingUp, TrendingDown } from 'lucide-react'
import { clsx, num, pct, changeClass, fa } from '../lib/format'
import { get, type Ticker as TickerData } from '../lib/api'

/* ---------------------------------------------------------------- آیکون ارز */
export function CoinIcon({ symbol, color, size = 38 }: { symbol: string; color?: string; size?: number }) {
  // نماد خالی یا تهی نباید کل صفحه را با استثنا زمین بزند
  const s = String(symbol ?? '')
  const c = color || '#7C5CFF'
  return (
    <div
      className="grid shrink-0 place-items-center rounded-full font-bold"
      style={{
        width: size, height: size,
        background: `radial-gradient(120% 120% at 30% 15%, ${c}44, ${c}18)`,
        border: `1px solid ${c}55`,
        color: c,
        fontSize: size * (s.length > 3 ? 0.26 : 0.32),
        boxShadow: `0 6px 22px -10px ${c}`,
      }}
    >
      {s.length <= 4 ? s : s.slice(0, 3)}
    </div>
  )
}

/* ---------------------------------------------------------------- نمودار کوچک */
export function Sparkline({ data, up, w = 110, h = 34 }: { data: number[]; up: boolean; w?: number; h?: number }) {
  const id = useMemo(() => 'sp' + Math.random().toString(36).slice(2), [])
  if (!data || data.length < 2) return <div style={{ width: w, height: h }} />
  const min = Math.min(...data)
  const max = Math.max(...data)
  const span = max - min || 1
  const pts = data.map((v, i) => [(i / (data.length - 1)) * w, h - ((v - min) / span) * (h - 4) - 2])
  const d = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ')
  const color = up ? '#00E39A' : '#FF4D6D'
  return (
    <svg width={w} height={h} className="overflow-visible">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${d} L${w},${h} L0,${h} Z`} fill={`url(#${id})`} />
      <path d={d} fill="none" stroke={color} strokeWidth="1.7" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  )
}

/* ---------------------------------------------------------------- جدول بازارها */
export function MarketTable({
  rows, favorites, onToggleFav, compactMode,
}: {
  rows: TickerData[]
  favorites?: string[]
  onToggleFav?: (s: string) => void
  compactMode?: boolean
}) {
  return (
    <>
      {/* ── موبایل: فهرست کارتی
          جدول شش‌ستونه روی گوشی یعنی ۶۸۰ پیکسل عرض، و مرورگر برای جا‌دادنش
          کل صفحه را کوچک می‌کند — همان چیزی که «مثل نسخه دسکتاپ» دیده می‌شود.
          اینجا همان داده در ارتفاع چیده می‌شود، نه در عرض. */}
      <div className="divide-y divide-white/[.05] md:hidden">
        {rows.map((t) => (
          <div key={t.symbol} className="flex items-center gap-3 px-3 py-3 active:bg-white/[.04]">
            {onToggleFav && (
              <button onClick={() => onToggleFav(t.symbol)}
                className="tap shrink-0 text-slate-600 active:text-gold">
                <Star className={clsx('h-4 w-4', favorites?.includes(t.symbol) && 'fill-gold text-gold')} />
              </button>
            )}
            <Link to={`/trade/${t.symbol}`} className="flex min-w-0 flex-1 items-center gap-3">
              <CoinIcon symbol={t.base} color={t.color} size={36} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-white">{t.faName}</div>
                <div className="num text-[11px] text-slate-500" dir="ltr">
                  {t.base}/{t.quote === 'IRT' ? 'TMN' : t.quote}
                </div>
              </div>
              <div className="shrink-0 text-left">
                <div className="num text-sm font-semibold text-white">{num(t.last)}</div>
                <div className={clsx('num inline-flex items-center gap-0.5 text-[11px] font-semibold', changeClass(t.change))}>
                  {t.change >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  {pct(t.change)}
                </div>
              </div>
              <div className="hidden shrink-0 xs:block"><Sparkline data={t.sparkline} up={t.change >= 0} w={64} h={28} /></div>
            </Link>
          </div>
        ))}
      </div>

      {/* ── دسکتاپ: همان جدول کامل */}
      <div className="hidden overflow-x-auto md:block">
      <table className="w-full min-w-[680px] text-sm">
        <thead>
          <tr className="text-xs text-slate-500">
            <th className="px-3 py-3 text-right font-medium">بازار</th>
            <th className="px-3 py-3 text-right font-medium">قیمت</th>
            <th className="px-3 py-3 text-right font-medium">تغییر ۲۴ ساعت</th>
            {!compactMode && <th className="px-3 py-3 text-right font-medium">بیشترین / کمترین</th>}
            {!compactMode && <th className="px-3 py-3 text-right font-medium">ارزش معاملات</th>}
            <th className="px-3 py-3 text-center font-medium">نمودار</th>
            <th className="px-3 py-3 text-left font-medium">عملیات</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.symbol} className="group border-t border-white/[.05] transition hover:bg-white/[.03]">
              <td className="px-3 py-3">
                <div className="flex items-center gap-3">
                  {onToggleFav && (
                    <button onClick={() => onToggleFav(t.symbol)} className="text-slate-600 transition hover:text-gold">
                      <Star className={clsx('h-4 w-4', favorites?.includes(t.symbol) && 'fill-gold text-gold')} />
                    </button>
                  )}
                  <CoinIcon symbol={t.base} color={t.color} size={34} />
                  <div>
                    <div className="font-semibold text-white">{t.faName}</div>
                    <div className="num text-xs text-slate-500" dir="ltr">{t.base}/{t.quote === 'IRT' ? 'TMN' : t.quote}</div>
                  </div>
                </div>
              </td>
              <td className="num px-3 py-3 font-semibold text-white">
                {num(t.last)} <span className="text-xs font-normal text-slate-500">{t.quote === 'IRT' ? 'تومان' : t.quote}</span>
              </td>
              <td className={clsx('num px-3 py-3 font-semibold', changeClass(t.change))}>
                <span className="inline-flex items-center gap-1">
                  {t.change >= 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                  {pct(t.change)}
                </span>
              </td>
              {!compactMode && (
                <td className="num px-3 py-3 text-xs text-slate-400">
                  <div className="text-up">{num(t.high)}</div>
                  <div className="text-down">{num(t.low)}</div>
                </td>
              )}
              {!compactMode && <td className="num px-3 py-3 text-slate-400">{num(t.quoteVolume)}</td>}
              <td className="px-3 py-3">
                <div className="flex justify-center">
                  <Sparkline data={t.sparkline} up={t.change >= 0} />
                </div>
              </td>
              <td className="px-3 py-3 text-left">
                <Link
                  to={`/trade/${t.symbol}`}
                  className="btn-ghost !px-3 !py-1.5 text-xs group-hover:border-brand-500/50 group-hover:text-brand-200"
                >
                  معامله
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </>
  )
}

/* ---------------------------------------------------------------- نوار قیمت متحرک */
export function Ticker({ rows }: { rows: TickerData[] }) {
  const items = rows.slice(0, 14)
  const doubled = [...items, ...items]
  return (
    <div className="relative overflow-hidden border-y border-white/[.06] bg-white/[.02] py-2.5">
      <div className="flex w-max animate-marquee gap-8">
        {doubled.map((t, i) => (
          <Link to={`/trade/${t.symbol}`} key={i} className="flex shrink-0 items-center gap-2 text-xs">
            <span className="font-semibold text-slate-300">{t.faName}</span>
            <span className="num text-slate-400">{num(t.last)}</span>
            <span className={clsx('num font-medium', changeClass(t.change))}>{pct(t.change)}</span>
          </Link>
        ))}
      </div>
    </div>
  )
}
